import { useEffect, useState, useCallback } from "react";
import { CheckCircle, AlertCircle, Info, X } from "lucide-react";

interface Toast {
  id: number;
  message: string;
  type: "success" | "error" | "info";
}

let nextId = 0;
let listeners: Array<(toasts: Toast[]) => void> = [];
let toasts: Toast[] = [];

function notify() {
  for (const listener of listeners) {
    listener([...toasts]);
  }
}

export function toast(message: string, type: Toast["type"] = "info") {
  const id = nextId++;
  toasts = [...toasts, { id, message, type }];
  notify();

  // Auto-dismiss
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    notify();
  }, 3000);
}

export function Toasts() {
  const [items, setItems] = useState<Toast[]>([]);

  useEffect(() => {
    listeners.push(setItems);
    return () => {
      listeners = listeners.filter((l) => l !== setItems);
    };
  }, []);

  const dismiss = useCallback((id: number) => {
    toasts = toasts.filter((t) => t.id !== id);
    notify();
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center gap-2 rounded-lg border border-border bg-surface-raised px-3 py-2 shadow-lg animate-in fade-in slide-in-from-bottom-2"
        >
          {item.type === "success" && <CheckCircle size={14} className="shrink-0 text-success" />}
          {item.type === "error" && <AlertCircle size={14} className="shrink-0 text-danger" />}
          {item.type === "info" && <Info size={14} className="shrink-0 text-accent" />}
          <span className="text-xs text-zinc-300">{item.message}</span>
          <button
            onClick={() => dismiss(item.id)}
            className="ml-2 shrink-0 text-zinc-600 hover:text-zinc-400"
          >
            <X size={10} />
          </button>
        </div>
      ))}
    </div>
  );
}
